import { Flex } from '@chakra-ui/react';
import {
  FormFieldARRIVEFiles,
  FormFieldCONSORTFiles,
  FormFieldFileUploadArray,
  FormFieldStandardReportingGuidelines,
  FormFieldStandardReportingGuidelinesDetails,
  FormFieldTextInput
} from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import { FieldHeader } from '../common/FieldHeader';
import StandardReportingGuidelinesUpdateModal from './StandardReportingGuidelinesUpdateModal';
import { StandardReportingGuidelinesDiffModal } from './StandardReportingGuidelinesDiffModal';
import { StandardReportingGuidelinesDisplay } from './StandardReportingGuidelinesDisplay';
import { StandardReportingGuidelinesEmptyState } from './StandardReportingGuidelinesEmptyState';
import {
  IAvailableWorkflowActions,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { getFileListCount } from 'src/utils/fileCount';

export type FormFieldBoolean = FormFieldStandardReportingGuidelines;

type IProps = {
  formFieldDefinition: FormFieldBoolean;
  value: boolean;
  previousValue?: boolean | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: boolean) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const StandardReportingGuidelinesField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated, getFieldState } =
    useSubmissionFields(entryPoint);
  const {
    fieldsResponse: { fields }
  } = useSubmissionData();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const StandardReportingGuidelinesDetails =
    getFieldState<FormFieldStandardReportingGuidelinesDetails>(
      'StandardReportingGuidelinesDetails'
    );
  const CONSORTFiles = getFieldState<FormFieldCONSORTFiles>('CONSORTFiles');
  const ARRIVEFiles = getFieldState<FormFieldARRIVEFiles>('ARRIVEFiles');

  const StandardReportingGuidelinesFileDetails = getFieldState<
    FormFieldFileUploadArray<'fileSet.StandardReportingGuidelinesFileDetails'>
  >('StandardReportingGuidelinesFileDetails');

  const StandardReportingGuidelinesTextConfig = getFieldState<
    FormFieldTextInput<'preference.standardReportingGuidelinesText'>
  >('StandardReportingGuidelinesText');

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated =
    checkFormFieldValueIsUpdated(formFieldDefinition.name) ||
    checkFormFieldValueIsUpdated('StandardReportingGuidelinesText');

  // Calculate total file count for Standard Reporting Guidelines
  const totalFileCount = value
    ? getFileListCount(StandardReportingGuidelinesFileDetails?.value) +
      getFileListCount(CONSORTFiles?.value) +
      getFileListCount(ARRIVEFiles?.value)
    : 0;

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <StandardReportingGuidelinesUpdateModal
          formField={formFieldDefinition}
          submissionFields={fields}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: boolean) => {
            setIsEditing(false);
            onChange(value);
          }}
          standardReportingGuidelinesDetails={
            StandardReportingGuidelinesDetails?.value
          }
          CONSORTFiles={CONSORTFiles?.value}
          ARRIVEFiles={ARRIVEFiles?.value}
          CONSORTFilesConfig={CONSORTFiles?.formFieldDefinition}
          ARRIVEFilesConfig={ARRIVEFiles?.formFieldDefinition}
          StandardReportingGuidelinesDetails={
            StandardReportingGuidelinesDetails?.formFieldDefinition ?? null
          }
          StandardReportingGuidelinesFileDetailsConfig={
            StandardReportingGuidelinesFileDetails?.formFieldDefinition ??
            undefined
          }
          StandardReportingGuidelinesFileDetails={
            StandardReportingGuidelinesFileDetails?.value
          }
          StandardReportingGuidelinesTextConfig={
            StandardReportingGuidelinesTextConfig?.formFieldDefinition ??
            undefined
          }
           StandardReportingGuidelinesText={
            StandardReportingGuidelinesTextConfig?.value ?? ''
          }
        />
      )}

      {isUpdated && showDiffModal ? (
        <StandardReportingGuidelinesDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
          standardReportingGuidelinesDetails={
            StandardReportingGuidelinesDetails?.value
          }
          CONSORTFiles={CONSORTFiles?.value}
          ARRIVEFiles={ARRIVEFiles?.value}
          previousStandardReportingGuidelinesDetails={
            fields['StandardReportingGuidelinesDetails']
              ?.previousValue as string[]
          }
          previousCONSORTFiles={
            fields['CONSORTFiles']?.previousValue as ISubmissionFile[]
          }
          previousARRIVEFiles={
            fields['ARRIVEFiles']?.previousValue as ISubmissionFile[]
          }
          StandardReportingGuidelinesFileDetails={
            StandardReportingGuidelinesFileDetails?.value
          }
          previousStandardReportingGuidelinesFileDetails={
            fields['StandardReportingGuidelinesFileDetails']
              ?.previousValue as ISubmissionFile[]
          }
          StandardReportingGuidelinesText={
            StandardReportingGuidelinesTextConfig?.value ?? ''
          }
          previousStandardReportingGuidelinesText={
            fields['StandardReportingGuidelinesText']?.previousValue as
              | string
              | undefined
          }
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
        fileCount={totalFileCount}
      />

      {typeof value === 'boolean' && value !== undefined ? (
        <StandardReportingGuidelinesDisplay
          value={value}
          subtitle={
            formFieldDefinition.params.labels.options?.[String(value)]?.main
          }
          standardReportingGuidelinesDetails={
            StandardReportingGuidelinesDetails?.value
          }
          CONSORTFiles={CONSORTFiles?.value}
          ARRIVEFiles={ARRIVEFiles?.value}
          StandardReportingGuidelinesFileDetails={
            StandardReportingGuidelinesFileDetails?.value
          }
          StandardReportingGuidelinesText={
            StandardReportingGuidelinesTextConfig?.value ?? ''
          }
        />
      ) : (
        <StandardReportingGuidelinesEmptyState />
      )}
    </Flex>
  );
};
